import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { EditorJobsService, type JobListItem } from '../../../features/editor/services/editor-jobs.service';
import { EditorFilesService } from '../../../features/editor/services/editor-files.service';

type JobFilter = 'all' | 'active' | 'done' | 'error';

@Component({
	selector: 'app-jobs-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [DatePipe],
	templateUrl: './jobs-dialog.html',
	styleUrl: './jobs-dialog.scss',
})
export class JobsDialogComponent {
	visible = input<boolean>(false);
	visibleChange = output<boolean>();

	protected jobsService = inject(EditorJobsService);
	private filesService = inject(EditorFilesService);
	protected filter = signal<JobFilter>('all');

	constructor() {
		// Refresh full job history when the panel opens
		effect(() => {
			if (this.visible()) {
				this.jobsService.loadAllJobs('all');
			}
		});
	}

	protected readonly filters: { key: JobFilter; label: string }[] = [
		{ key: 'all', label: 'All' },
		{ key: 'active', label: 'Running' },
		{ key: 'done', label: 'Completed' },
		{ key: 'error', label: 'Failed' },
	];

	protected filteredJobs = computed<JobListItem[]>(() => {
		const all = Array.from(this.jobsService.jobs().values());
		const f = this.filter();
		const filtered = all.filter(j => {
			if (f === 'all') return true;
			if (f === 'active') return j.status === 'pending' || j.status === 'running';
			return j.status === f;
		});
		// Sort: active first, then by createdAt desc
		return filtered.sort((a, b) => {
			const aActive = a.status === 'pending' || a.status === 'running' ? 0 : 1;
			const bActive = b.status === 'pending' || b.status === 'running' ? 0 : 1;
			if (aActive !== bActive) return aActive - bActive;
			return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
		});
	});

	protected counts = computed(() => {
		const all = Array.from(this.jobsService.jobs().values());
		return {
			all: all.length,
			active: all.filter(j => j.status === 'pending' || j.status === 'running').length,
			done: all.filter(j => j.status === 'done').length,
			error: all.filter(j => j.status === 'error').length,
		};
	});

	onVisibleChange(value: boolean): void {
		this.visibleChange.emit(value);
	}

	protected dismissJob(jobId: string): void {
		this.jobsService.dismissJob(jobId);
	}

	protected cancelJob(jobId: string): void {
		this.jobsService.cancelJob(jobId);
	}

	protected progressPercent(job: JobListItem): number {
		return Math.round(job.progress * 100);
	}

	protected isActive(job: JobListItem): boolean {
		return job.status === 'pending' || job.status === 'running';
	}

	/** A job targets a variant group when the resolved media has `variants`
	 * populated. For those we collapse the dialog row to a single aggregate
	 * progress bar (plan D7) — hiding the verbose per-variant `currentStep` and
	 * the `N versions generated` count which both explode combinatorially when
	 * the group has many variants × widths × codecs. */
	protected isVariantGroupJob(job: JobListItem): boolean {
		const media = this.filesService.files().find(f => f.id === job.mediaId);
		return !!media?.variants?.length;
	}

	/** Poster-generation jobs don't produce "versions" — they write a single
	 * thumbnail. Used to swap the "N version(s) generated" done-label for a
	 * thumbnail-appropriate one (the count is always 0 for these). */
	protected isThumbnailJob(job: JobListItem): boolean {
		return job.type === 'thumbnail';
	}
}
